<template>
  <v-card
    class="client-card elevation-0"
    :class="{ 'has-issues': hasIssues }"
    height="100%"
  >
    <!-- Card Header -->
    <v-card-title class="subtitle-1 font-weight-bold pb-2">
      <v-col>
        <v-row>
          <v-col
            class="pb-0 col-grow text-truncate clickable"
            :title="client.clientName"
            @click="$emit('view-profile', client)"
          >
            <span class="darkblue--text">
              {{ client.clientName || 'Name Unknown' }}
            </span>
          </v-col>

          <v-col class="col-auto pb-0 px-0 text-right">
            <!-- Status Chip -->
            <v-chip
              :color="clientStatusColor"
              :class="`ml-2 white--text`"
              small
            >
              {{ clientStatusText }}
            </v-chip>
          </v-col>

          <v-col class="col-auto pb-0 pt-2 px-0">
            <!-- Actions Menu -->
            <v-menu
              offset-y
              bottom
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="['fas', 'ellipsis-vertical']"
                    size="lg"
                  />
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click="$emit('view-profile', client)">
                  <v-list-item-title>View Profile</v-list-item-title>
                </v-list-item>

                <template v-if="isAdmin">
                  <v-divider />
                  <v-list-item @click="$emit('edit', client)">
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="$emit('delete', client)">
                    <v-list-item-title class="error--text">
                      Delete
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="pt-0">
            <v-row class="body-2 mt-0">
              <v-col class="col-auto py-0 font-weight-medium darkblue--text">
                {{ client.billingCode }}
              </v-col>

              <v-col
                v-if="client.clientId"
                class="col-auto pa-0"
              >
                <font-awesome-icon
                  :icon="['fas', 'circle']"
                  size="xs"
                  class="px-1 midgray--text"
                  style="font-size: 6px; margin-bottom: 1.5px;"
                />
              </v-col>

              <v-col
                v-if="client.clientId"
                class="col-auto py-0 font-weight-medium darkblue--text"
              >
                ID: {{ client.clientId }}
              </v-col>
            </v-row>

            <v-row v-if="client.networkCode">
              <v-col class="col-auto py-0 font-weight-medium darkblue--text">
                {{ client.networkCode }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-card-title>

    <!-- Card Content -->
    <v-card-text>
      <v-col>
        <!-- Features Row -->
        <v-row v-if="client.isTimesheetEnabled || hasIssues">
          <v-col class="py-1">
            <v-chip
              v-if="client.isTimesheetEnabled"
              small
              color="info"
              text-color="white"
              class="mr-2"
            >
              <font-awesome-icon
                :icon="['fas', 'clock']"
                class="mr-1"
              />
              Timesheet
            </v-chip>

            <v-chip
              v-if="hasIssues"
              small
              color="error"
              text-color="white"
            >
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                class="mr-1"
              />
              Issues
            </v-chip>
          </v-col>
        </v-row>

        <!-- Config Section -->
        <v-row v-if="loadingConfig">
          <v-col class="pt-4">
            <v-row>
              <v-col class="pb-2 midgray--text subtitle-2">
                Configuration
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-skeleton-loader
                  type="heading"
                  :loading="loadingConfig"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col>
            <v-row class="subtitle-2">
              <v-col class="pb-2 midgray--text">
                Configuration
              </v-col>
            </v-row>

            <v-row
              v-if="clientConfig"
              class="body-2 mt-0"
            >
              <v-col class="pt-0 font-weight-medium darkblue--text">
                <div class="d-flex align-center mb-1">
                  <font-awesome-icon
                    :icon="configActiveIcon"
                    :class="configActiveClass"
                    class="mr-2"
                    size="sm"
                    fixed-width
                  />
                  <span>{{ configActiveText }}</span>
                </div>

                <div
                  v-if="clientConfig.enterpriseId"
                  class="d-flex align-center mb-1"
                >
                  <font-awesome-icon
                    :icon="['fas', 'id-card']"
                    class="mr-2 midgray--text"
                    size="sm"
                    fixed-width
                  />
                  <span class="text-caption">{{ clientConfig.enterpriseId }}</span>
                </div>

                <!-- Permissions -->
                <div
                  v-if="hasPermissions"
                  class="d-flex align-center"
                >
                  <font-awesome-icon
                    :icon="['fas', 'key']"
                    class="mr-2 midgray--text"
                    size="sm"
                    fixed-width
                  />
                  <div>
                    <v-chip
                      v-if="permissions.pacing"
                      x-small
                      outlined
                      class="mr-1"
                    >
                      Pacing
                    </v-chip>
                    <v-chip
                      v-if="permissions.discrepancy"
                      x-small
                      outlined
                      class="mr-1"
                    >
                      Discrepancy
                    </v-chip>
                    <v-chip
                      v-if="permissions.customReports && permissions.customReports.length > 0"
                      x-small
                      outlined
                    >
                      Reports ({{ permissions.customReports.length }})
                    </v-chip>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row
              v-else
              class="body-2 mt-0"
            >
              <v-col class="pt-0 font-weight-medium midgray--text">
                No configuration found
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Publisher Section -->
        <v-row v-if="loadingPublisher">
          <v-col class="pt-4">
            <v-row>
              <v-col class="pb-2 midgray--text subtitle-2">
                Publisher
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-skeleton-loader
                  type="heading"
                  :loading="loadingPublisher"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col>
            <v-row class="subtitle-2">
              <v-col class="pb-2 midgray--text">
                Publisher
              </v-col>
            </v-row>

            <v-row
              v-if="clientPublisher"
              class="body-2 mt-0"
            >
              <v-col class="pt-0 font-weight-medium darkblue--text">
                <div class="d-flex align-items-center mb-1">
                  <font-awesome-icon
                    :icon="['fas', 'building']"
                    class="mr-2 midgray--text"
                    size="sm"
                    fixed-width
                  />
                  <span>{{ clientPublisher.name || 'N/A' }}</span>
                </div>

                <div
                  v-if="clientPublisher.publisherId"
                  class="d-flex align-items-center mb-1"
                >
                  <font-awesome-icon
                    :icon="['fas', 'hashtag']"
                    class="mr-2 midgray--text"
                    size="sm"
                    fixed-width
                  />
                  <span class="text-caption">{{ clientPublisher.publisherId }}</span>
                </div>

                <div
                  v-if="teamCount > 0"
                  class="d-flex align-items-center"
                >
                  <font-awesome-icon
                    :icon="['fas', 'users']"
                    class="mr-2 midgray--text"
                    size="sm"
                    fixed-width
                  />
                  <span class="text-caption">{{ teamCount }} team{{ teamCount > 1 ? 's' : '' }}</span>
                </div>
              </v-col>
            </v-row>

            <v-row
              v-else
              class="body-2 mt-0"
            >
              <v-col class="pt-0 font-weight-medium midgray--text">
                No publisher found
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Issues Warning -->
        <v-row v-if="hasIssues">
          <v-col class="pt-2">
            <v-alert
              dense
              text
              type="warning"
              class="mb-0 caption"
            >
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                class="mr-1"
              />
              Configuration issue detected
            </v-alert>
          </v-col>
        </v-row>
      </v-col>
    </v-card-text>

    <!-- Quick View Button -->
    <v-divider />
    <v-card-actions>
      <v-btn
        text
        small
        color="primary"
        @click="$emit('view-profile', client)"
      >
        <font-awesome-icon
          :icon="['fas', 'arrow-right']"
          class="mr-1"
        />
        View Details
      </v-btn>
      <v-spacer />
      <v-chip
        v-if="client.isAdministrative"
        x-small
        outlined
      >
        Internal Use
      </v-chip>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: 'ClientCard',
  props: {
    client: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      default: null,
    },
    publisher: {
      type: Object,
      default: null,
    },
    loadingConfig: {
      type: Boolean,
      default: false,
    },
    loadingPublisher: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /**
     * Get client config from prop
     */
    clientConfig() {
      return this.config
    },

    /**
     * Get client publisher from prop
     */
    clientPublisher() {
      return this.publisher
    },

    /**
     * Client status color
     */
    clientStatusColor() {
      const { isActive } = this.client
      const configActive = this.clientConfig?.access?.active !== false

      if (isActive && configActive) return 'success'
      if (!isActive) return 'error'
      if (!configActive) return 'warning'

      return 'grey'
    },

    /**
     * Client status icon
     */
    clientStatusIcon() {
      const { isActive } = this.client
      const configActive = this.clientConfig?.access?.active !== false

      if (isActive && configActive) return ['fas', 'check-circle']
      if (!isActive) return ['fas', 'ban']
      if (!configActive) return ['fas', 'exclamation-circle']

      return ['fas', 'question-circle']
    },

    /**
     * Client status text
     */
    clientStatusText() {
      const { isActive } = this.client
      const configActive = this.clientConfig?.access?.active !== false

      if (isActive && configActive) return 'Active'
      if (!isActive) return 'Inactive'
      if (!configActive) return 'Config Inactive'

      return 'Unknown'
    },

    /**
     * Config active status icon
     */
    configActiveIcon() {
      const isActive = this.clientConfig?.access?.active !== false
      return isActive ? ['fas', 'check-circle'] : ['fas', 'times-circle']
    },

    /**
     * Config active status class
     */
    configActiveClass() {
      const isActive = this.clientConfig?.access?.active !== false
      return isActive ? 'success--text' : 'error--text'
    },

    /**
     * Config active status text
     */
    configActiveText() {
      const isActive = this.clientConfig?.access?.active !== false
      return isActive ? 'Active' : 'Inactive'
    },

    /**
     * Permissions from config
     */
    permissions() {
      return this.clientConfig?.access?.permissions || {}
    },

    /**
     * Has any permissions
     */
    hasPermissions() {
      const { pacing, discrepancy, customReports } = this.permissions
      return pacing || discrepancy || (customReports && customReports.length > 0)
    },

    /**
     * Team count from publisher
     */
    teamCount() {
      if (!this.clientPublisher) return 0

      const gamTeams = this.clientPublisher.teams?.length || 0
      const productTeams = this.clientConfig?.settings?.teams?.length || 0

      return gamTeams + productTeams
    },

    /**
     * Check if client has issues
     */
    hasIssues() {
      const hasNoConfig = !this.clientConfig && !this.loadingConfig
      const hasNoPublisher = !this.clientPublisher && !this.loadingPublisher

      const hasMismatch = this.client.clientId
        && this.clientPublisher?.publisherId
        && this.client.clientId !== this.clientPublisher.publisherId

      return hasNoConfig || hasNoPublisher || hasMismatch
    },
  },
}
</script>

<style lang="scss" scoped>
.client-card {
  transition: box-shadow 0.2s ease, transform 0.1s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(-1px);
  }

  &.has-issues {
    border-left: 3px solid var(--v-error-base);
  }

  .clickable {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  .col-grow {
    flex-grow: 1;
    min-width: 0;
  }

  .v-card__title {
    .v-row {
      margin-left: -8px;
      margin-right: -8px;
    }
  }

  .v-card__text {
    flex-grow: 1;

    .subtitle-2 {
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-size: 0.7rem;
    }
  }
}

// Dark mode support
.theme--dark {
  .client-card {
    border-color: rgba(255, 255, 255, 0.12);

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
    }

    &.has-issues {
      border-left-color: var(--v-error-lighten1);
    }
  }
}
</style>
